/* ================================================================
   market.ts — the shape of a market profile, and the formatting that
   depends on it. Pure functions of a profile: nothing here knows which
   market is being built (that is src/config/region.ts), so a market's
   own content pack can use them without importing the selection.
   ================================================================ */

export interface SocialLink {
  label: string;
  href: string;
}

/** One market the site is published in, as every market's switcher and
    hreflang alternates name it. */
export interface MarketLink {
  code: string;
  /** Shown in the footer's country switcher, e.g. "United States". */
  name: string;
  /** Emoji flag shown before the name. */
  flag: string;
  /** hreflang value for this market's pages, e.g. en-US. */
  hreflang: string;
  /** That market's canonical origin, no trailing slash. */
  baseUrl: string;
  /** Routes that market does not have (no alternate is declared for them, and
      the switcher sends a visitor on one of them to that market's homepage). */
  omitRoutes: string[];
}

export interface SiteNavigationEntry {
  name: string;
  path: string;
  description: string;
}

/** src/config/regions/<code>/region.json. */
export interface RegionProfile {
  code: string;
  name: string;
  /** ISO 3166-1 alpha-2; JSON-LD areaServed. */
  country: string;
  /** BCP 47, e.g. en-AU. */
  locale: string;
  /** ISO 4217 code prices are published in. */
  currency: string;
  timezone: string;
  formats: {
    /** Locale money is written in; picks the symbol (under 'en', AUD is A$). */
    moneyLocale: string;
    /** Locale dates such as a blog post's are written in. */
    dateLocale: string;
  };
  site: {
    name: string;
    /** Canonical origin, no trailing slash. */
    baseUrl: string;
    /** Default share image, as a path under baseUrl. */
    image: string;
    /** Open Graph locale, e.g. en_AU. */
    ogLocale: string;
    twitter: string;
    gaMeasurementId: string;
    keywords: string;
    organizationDescription: string;
  };
  apps: {
    /** Where sign-in hands off to and every "get started" link points. */
    juristab: string;
    docudive: string;
  };
  contact: {
    email: string;
    /** As displayed, e.g. "+61 370 48 48 42"; '' when the market has no phone line (it is then not shown). */
    phone: string;
    location: string;
  };
  /** The office the Contact page maps; null when the market has none to show. */
  office: {
    label: string;
    mapTitle: string;
    latitude: number;
    longitude: number;
    directionsUrl: string;
  } | null;
  social: SocialLink[];
  calculator: {
    /** Offered when the calculator API cannot list its currencies. */
    currencies: string[];
  };
  /** Main sections, declared to search engines as SiteNavigationElement. */
  siteNavigation: SiteNavigationEntry[];
  /**
   * Every market the site ships in — the same block in every market's
   * region.json (src/config/__tests__/markets.test.ts keeps them identical).
   * Drives the hreflang alternates, the footer's country switcher and the
   * cookie that tells the edge's geo-redirect which market a visitor chose.
   */
  markets: {
    /** Code of the market hreflang="x-default" points at. */
    xDefault: string;
    /** Set before following a switcher link: <name>=<code>. */
    cookie: { name: string; domain: string; maxAgeSeconds: number };
    list: MarketLink[];
  };
}

/** An amount in the market's currency, e.g. money(region, 59) → "A$59". */
export function formatMoney(region: RegionProfile, amount: number, decimals = 0): string {
  return new Intl.NumberFormat(region.formats.moneyLocale, {
    style: 'currency',
    currency: region.currency,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

/** The bare symbol shown before a plan's price, e.g. "$" for AUD, "£" for GBP. */
export function currencySymbol(region: RegionProfile): string {
  const symbol = (currencyDisplay: 'narrowSymbol' | 'symbol') =>
    new Intl.NumberFormat(region.formats.moneyLocale, {
      style: 'currency',
      currency: region.currency,
      currencyDisplay,
    })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value;
  try {
    return symbol('narrowSymbol') ?? region.currency;
  } catch {
    // Browsers that predate 'narrowSymbol' (Safari < 14.1) throw on it.
    return symbol('symbol') ?? region.currency;
  }
}

/** A calendar date, e.g. a blog post's: "Sep 1, 2026" under en-US. */
export function formatDate(region: RegionProfile, date: Date): string {
  return date.toLocaleDateString(region.formats.dateLocale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** The market's own entry in region.markets.list. */
export function ownMarket(region: RegionProfile): MarketLink {
  const own = region.markets.list.find((m) => m.code === region.code);
  if (!own) throw new Error(`region.json markets.list has no entry for "${region.code}"`);
  return own;
}

/**
 * A market's URL for the page at `path`: the same section, or its homepage
 * when it has no such section. Only top-level sections are carried over —
 * a blog post or calculator variant need not exist in the other market, so
 * /blogs/<slug> goes to that market's /blogs.
 */
export function marketUrl(market: MarketLink, path: string): string {
  const section = `/${(path || '/').split('/')[1] ?? ''}`;
  return market.omitRoutes.includes(section) ? `${market.baseUrl}/` : `${market.baseUrl}${section}`;
}

export interface Alternate {
  hreflang: string;
  href: string;
}

/**
 * hreflang alternates for one route: every market that has the route, plus
 * x-default. A route only one market has gets none — an alternate set of one
 * says nothing. scripts/region.js computes the same list for the prerendered
 * HTML and the sitemap (src/config/__tests__/markets.test.ts checks both agree).
 */
export function alternatesFor(region: RegionProfile, path: string): Alternate[] {
  const route = path === '' ? '/' : path;
  const having = region.markets.list.filter((m) => !m.omitRoutes.includes(route));
  if (having.length < 2) return [];
  const url = (m: MarketLink) => `${m.baseUrl}${route}`;
  const out = having.map((m) => ({ hreflang: m.hreflang, href: url(m) }));
  const fallback = having.find((m) => m.code === region.markets.xDefault);
  if (fallback) out.push({ hreflang: 'x-default', href: url(fallback) });
  return out;
}

/** The Set-Cookie string that records a visitor's market choice. */
export function marketCookie(region: RegionProfile, code: string): string {
  const { name, domain, maxAgeSeconds } = region.markets.cookie;
  return `${name}=${code}; Domain=${domain}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax; Secure`;
}

/** tel: link for a displayed number: "+61 370 48 48 42" → "tel:+61370484842". */
export function phoneHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, '')}`;
}
